import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const StudentsImport = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=Upload, 2=Preview, 3=Done
  const [file, setFile] = useState(null);
  const [academicYear, setAcademicYear] = useState('2026-2027');
  const [academicYears, setAcademicYears] = useState([]);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    fetchAcademicYears();
  }, []);

  const fetchAcademicYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/admin/academic-years', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const years = Array.isArray(res.data) ? res.data : [];
      setAcademicYears(years);
      const current = years.find((y) => y.is_current && !y.is_closed);
      if (current) setAcademicYear(current.year);
    } catch (err) {
      console.error(err);
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/admin/students/template', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modele_import_eleves.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('❌ Erreur téléchargement : ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePreview = async () => {
    if (!file) {
      alert('Sélectionnez un fichier');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('academic_year', academicYear);

      const res = await api.post('/admin/students/import/preview', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });

      setPreview(res.data.preview || []);
      setErrors(res.data.errors || []);
      setStep(2);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!window.confirm(`Importer ${preview?.length || 0} élève(s) ?`)) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('academic_year', academicYear);

      const res = await api.post('/admin/students/import', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });

      setResult(res.data.results);
      setStep(3);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setErrors([]);
    setResult(null);
  };

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0A0A]">📥 Importer des élèves</h1>
          <p className="text-gray-500 mt-1">
            Importez plusieurs élèves en une seule fois à partir d'un fichier Excel
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/students')}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
        >
          ← Retour
        </button>
      </div>

      {/* ÉTAPES */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
              step >= s ? 'bg-[#F39C12] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > s ? '✓' : s}
            </div>
            {s < 3 && (
              <div className={`flex-1 h-1 rounded ${step > s ? 'bg-[#F39C12]' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ÉTAPE 1 : UPLOAD */}
      {step === 1 && (
        <>
          <div className="bg-blue-50 rounded-2xl border border-blue-200 p-6 mb-6">
            <h3 className="font-bold text-blue-800 mb-2">📄 Étape 1 : Téléchargez le modèle Excel</h3>
            <p className="text-sm text-blue-700 mb-4">
              Utilisez notre modèle pour être sûr que toutes les colonnes sont au bon format.
            </p>
            <button
              onClick={downloadTemplate}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-medium"
            >
              📥 Télécharger le modèle Excel
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="font-bold text-lg mb-4">📤 Étape 2 : Uploadez votre fichier</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Année scolaire
              </label>
              <select
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                className="w-full md:w-64 px-3 py-2 border rounded-lg"
              >
                <option value="">-- Choisir --</option>
                {academicYears.filter((y) => !y.is_closed).map((y) => (
                  <option key={y.id} value={y.year}>
                    {y.year} {y.is_current ? '✅ (en cours)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fichier Excel (.xlsx, .xls)
              </label>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setFile(e.target.files[0])}
                className="w-full px-3 py-2 border rounded-lg"
              />
              {file && (
                <p className="text-sm text-green-600 mt-2">
                  ✅ Fichier sélectionné : {file.name} ({(file.size / 1024).toFixed(1)} Ko)
                </p>
              )}
            </div>

            <div className="bg-yellow-50 rounded-lg p-4 mb-4 text-sm text-yellow-800">
              <p className="font-bold mb-1">💡 Conseils :</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Une ligne = un élève</li>
                <li>Le téléphone du parent est utilisé pour <strong>fusionner les comptes</strong></li>
                <li>Si 3 enfants ont le même téléphone → 1 seul compte parent</li>
                <li>Format de date : <code>AAAA-MM-JJ</code> ou <code>JJ/MM/AAAA</code></li>
              </ul>
            </div>

            <button
              onClick={handlePreview}
              disabled={loading || !file || !academicYear}
              className="w-full bg-[#F39C12] text-white py-3 rounded-lg hover:bg-[#E67E22] font-bold disabled:opacity-50"
            >
              {loading ? '⏳ Analyse...' : '🔍 Analyser le fichier'}
            </button>
          </div>
        </>
      )}

      {/* ÉTAPE 2 : PREVIEW */}
      {step === 2 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 rounded-xl border border-green-200 p-4">
              <p className="text-xs text-green-700 mb-1">✅ Élèves valides</p>
              <p className="text-2xl font-bold text-green-600">{preview?.length || 0}</p>
            </div>
            <div className="bg-red-50 rounded-xl border border-red-200 p-4">
              <p className="text-xs text-red-700 mb-1">⚠️ Erreurs</p>
              <p className="text-2xl font-bold text-red-600">{errors?.length || 0}</p>
            </div>
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
              <p className="text-xs text-blue-700 mb-1">📅 Année</p>
              <p className="text-2xl font-bold text-blue-600">{academicYear}</p>
            </div>
          </div>

          {preview?.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
              <div className="p-4 bg-green-50 border-b">
                <h3 className="font-bold text-green-800">✅ Élèves à importer ({preview.length})</h3>
              </div>
              <div className="overflow-x-auto max-h-96">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Nom</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Classe</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Parent</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Parent existant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((p, i) => (
                      <tr key={i} className="border-t hover:bg-gray-50">
                        <td className="py-2 px-3 text-sm font-medium">{p.nom_complet}</td>
                        <td className="py-2 px-3 text-sm">{p.classe}</td>
                        <td className="py-2 px-3 text-sm">{p.parent}</td>
                        <td className="py-2 px-3">
                          {p.parent_exists ? (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">🔗 Sera relié</span>
                          ) : (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">✨ Sera créé</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {errors?.length > 0 && (
            <div className="bg-white rounded-2xl border border-red-200 overflow-hidden mb-6">
              <div className="p-4 bg-red-50 border-b">
                <h3 className="font-bold text-red-800">⚠️ Erreurs à corriger ({errors.length})</h3>
                <p className="text-xs text-red-600 mt-1">
                  Ces lignes ne seront PAS importées. Corrigez le fichier et recommencez.
                </p>
              </div>
              <div className="overflow-x-auto max-h-64">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Ligne</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Nom</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">Erreur</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.map((e, i) => (
                      <tr key={i} className="border-t">
                        <td className="py-2 px-3 text-sm font-mono">L{e.row}</td>
                        <td className="py-2 px-3 text-sm">{e.nom}</td>
                        <td className="py-2 px-3 text-sm text-red-600">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={resetAll}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 font-medium"
            >
              ← Recommencer
            </button>
            <button
              onClick={handleImport}
              disabled={loading || !preview?.length}
              className="flex-1 bg-green-500 text-white py-3 rounded-lg hover:bg-green-600 font-bold disabled:opacity-50"
            >
              {loading ? '⏳ Import...' : `🎯 Importer ${preview?.length || 0} élève(s)`}
            </button>
          </div>
        </>
      )}

      {/* ÉTAPE 3 : DONE */}
      {step === 3 && result && (
        <div className="bg-white rounded-2xl border border-gray-200 p-8">
          <div className="text-center mb-8">
            <p className="text-6xl mb-4">🎉</p>
            <h2 className="text-2xl font-bold text-[#0A0A0A] mb-2">Import terminé !</h2>
            <p className="text-gray-500">Les élèves ont été ajoutés avec succès</p>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <p className="text-xs text-green-700 mb-1">👥 Élèves créés</p>
              <p className="text-3xl font-bold text-green-600">{result.students_created}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-xs text-blue-700 mb-1">✨ Parents créés</p>
              <p className="text-3xl font-bold text-blue-600">{result.parents_created}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <p className="text-xs text-purple-700 mb-1">🔗 Parents reliés</p>
              <p className="text-3xl font-bold text-purple-600">{result.parents_linked}</p>
            </div>
          </div>

          {result.errors?.length > 0 && (
            <div className="bg-red-50 rounded-xl border border-red-200 p-4 mb-6">
              <p className="font-bold text-red-800 mb-2">⚠️ {result.errors.length} erreur(s)</p>
              <ul className="text-sm text-red-700 list-disc ml-5">
                {result.errors.slice(0, 5).map((e, i) => (
                  <li key={i}>Ligne {e.row} — {e.nom} : {e.error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={resetAll}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 font-medium"
            >
              📥 Importer un autre fichier
            </button>
            <button
              onClick={() => navigate('/admin/students')}
              className="flex-1 bg-[#F39C12] text-white py-3 rounded-lg hover:bg-[#E67E22] font-bold"
            >
              👥 Voir les élèves →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsImport;